import { storeToRefs } from 'pinia';
import LOCATIONS_MAP from '@/common/constants/locationsMap.constants';
import {
  NODES_FAILURE_DOMAINS,
  NODES_STATUS_MAP,
  NODES_STATUSES_V3
} from '@/common/constants/nodes.constants';
import { TaskState, NodeV3 } from '@schema';
import { sortBy, uniq } from 'lodash-es';
import { STATUS_ICON_LOADING } from '@/common/utils/statuses';
import useStorageClusterStore from '@/common/store/pinia/storageCluster';
import useVhsNodesStore from '@/common/store/pinia/vhsNodes';
import i18n from '@/common/i18n';
import mergeFn from '@/common/utils/merge';
import ip from 'ip';
import {
  WEBCP,
  MDS,
  CS,
  COMPUTE,
  ABGW,
  ISCSI,
  S3,
  NFS,
  FILE_STORAGE,
  VHS_COMPUTE,
  NODE_SERVICE_STATUS,
  NODE_SERVICE_TYPE,
  NODE_SERVICE_TAG_TYPE
} from '@/common/constants/services.constants';
import { COMPUTE_ROLES, COMPUTE_ROLE_TAG_TEXT } from '@/common/constants/computeNodes.constants';
import mergeNodeIpAddresses from '@/common/utils/mergeNodeIpAddresses';
import { determineMaintenanceStatus, isNodeInProgress, defineStatus, getServiceState } from './helpers';

const serviceStateMap = {
  failed: 0,
  maintenance: 1,
  healthy: 2
};

const getInitialState = () => ({
  activeNotifications: false,
  nodes: [],
  locations: [],
  loading: false,
});

const getIpsByRole = (node, roles) => {
  const ips = ['4', '6'].flatMap(version => roles.flatMap(role => node.ip_addrs?.[version]?.[role])).filter(Boolean);
  return ips.length ? uniq(ips) : undefined;
};

function prepareNode(node, getters) {
  const spaces = Object.entries(node.spaces || {}).reduce((memo, [tier, space]) => {
    if (space.t > 0) {
      memo[tier] = {
        used: space.t - space.f,
        available: space.a,
        total: space.t,
        ratio: ((space.t - space.f) / space.t) * 100
      };
    }
    return memo;
  }, {});
  const initialServices = node.services || {};
  const nodeServices = Object.values(initialServices).filter(Boolean);
  const isUnassigned = node.status.includes('unassigned');
  const isFailed = !!nodeServices.find(({ f }) => f === true);
  const isOffline = node.status.includes('offline');
  const isPrimary = node.status.includes('primary');
  const isInHa = node.status.includes('ha');
  const maintenanceStatus = determineMaintenanceStatus(node);
  const status = defineStatus(node);
  const inProgress = isNodeInProgress(node);
  const isMaintenanceComplete = maintenanceStatus === NODES_STATUSES_V3.MAINTENANCE;
  const services = [];
  const rackName = getters.getRackById(node.rackId)?.name;
  const rowName = getters.getRowById(node.rowId)?.name;
  const roomName = getters.getRoomById(node.roomId)?.name;
  // eslint-disable-next-line camelcase
  const managementIp = node.ip_addrs?.['4']?.web_cp?.[0];
  const isOnline = !isOffline;
  const isMaintenance = !!maintenanceStatus;
  const isHealthy = status === NODES_STATUSES_V3.HEALTHY;
  const isAssigned = !isUnassigned;
  const isInRecovery = node?.recovery?.status === NodeV3.status.RECOVERING || node?.recovery?.status === NodeV3.status.FAILED;

  if (isPrimary) {
    services.push({
      key: WEBCP,
      title: i18n.t('Admin panel'),
      type: NODE_SERVICE_TYPE.INTERNAL,
      state: NODE_SERVICE_STATUS.HEALTHY
    });
  }

  [
    {
      key: ABGW,
      title: i18n.t('Backup storage'),
      type: NODE_SERVICE_TYPE.EXTERNAL
    },
    {
      key: ISCSI,
      title: i18n.t('Block storage'),
      type: NODE_SERVICE_TYPE.EXTERNAL
    },
    {
      key: S3,
      title: i18n.t('Object storage'),
      type: NODE_SERVICE_TYPE.EXTERNAL
    },
    {
      key: NFS,
      title: i18n.t('NFS'),
      type: NODE_SERVICE_TYPE.EXTERNAL
    },
    {
      key: CS,
      title: i18n.t('Storage'),
      type: NODE_SERVICE_TYPE.INTERNAL
    },
    {
      key: MDS,
      title: initialServices.mds?.m ? i18n.t('Primary metadata') : i18n.t('Metadata'),
      type: NODE_SERVICE_TYPE.INTERNAL
    },
    {
      key: FILE_STORAGE,
      title: i18n.t('File storage'),
      type: NODE_SERVICE_TYPE.EXTERNAL
    },
  ].forEach(({ key, title, type }) => {
    if (initialServices[key]) {
      services.push({
        key,
        title,
        type,
        state: getServiceState(initialServices[key], isMaintenanceComplete)
      });
    }
  });

  if (initialServices.compute?.roles) {
    COMPUTE_ROLES.forEach(key => {
      if (initialServices.compute?.roles[`is_${key}`]) {
        services.push({
          key,
          title: COMPUTE_ROLE_TAG_TEXT[key],
          type: NODE_SERVICE_TYPE.EXTERNAL,
          state: getServiceState(initialServices.compute, isMaintenanceComplete)
        });
      }
    });
  }

  const vhsNode = useVhsNodesStore().itemsMap[node.id];
  const isInVhsCompute = !!vhsNode && vhsNode.status !== 'MISSING';
  if (isInVhsCompute) {
    services.push({
      key: VHS_COMPUTE,
      title: i18n.t('VHS compute'),
      type: NODE_SERVICE_TYPE.EXTERNAL,
      state: vhsNode.status === 'ERROR' ? NODE_SERVICE_STATUS.FAILED : NODE_SERVICE_STATUS.HEALTHY
    });
  }

  services.sort((a, b) => serviceStateMap[a.state] - serviceStateMap[b.state]);

  const ipAddresses = mergeNodeIpAddresses({ ...node.ip_addrs['4'] }, { ...node.ip_addrs['6'] });
  const ipV4Addresses = node.ip_addrs['4'] ? uniq(Object.values(node.ip_addrs['4']).flat()) : [];
  const ipV6Addresses = node.ip_addrs['6'] ? uniq(Object.values(node.ip_addrs['6']).flat()) : [];
  const ipAddressesList = [...ipV4Addresses, ...ipV6Addresses];

  const s3Ip = getIpsByRole(node, ['s3_public', 'ostor_private']);

  return {
    id: node.id,
    name: node.orig_hostname || node.host,
    host: node.host,
    internalName: node.host.replace('.vstoragedomain', ''),
    inProgress,
    isAssigned,
    isAvailable: isOnline && !!ipAddressesList.length && (isHealthy || inProgress || isMaintenanceComplete),
    isComputeController: !!node.services[COMPUTE]?.roles?.is_controller,
    isComputeWorker: !!node.services[COMPUTE]?.roles?.is_compute,
    isFailed,
    isHealthy,
    isInABGW: !!node.services[ABGW],
    isInCompute: !!node.services[COMPUTE],
    isInHa,
    isInNFS: !!node.services[NFS],
    isInRecovery,
    isInS3: !!node.services[S3],
    isInStorage: !!node.services[CS],
    isInISCSI: !!node.services[ISCSI],
    isInFileStorage: !!node.services[FILE_STORAGE],
    isMaintenance,
    isMaintenanceComplete,
    isMaintenanceEnteringIncomplete: status === NODES_STATUSES_V3.MAINTENANCE_SUSPENDING_INCOMPLETE,
    isMaintenanceExitingIncomplete: status === NODES_STATUSES_V3.MAINTENANCE_RESUMING_INCOMPLETE,
    isMaintenanceSuspending: status === NODES_STATUSES_V3.MAINTENANCE_SUSPENDING,
    isOffline,
    isOnline,
    isOperational: isOnline && !isFailed,
    isPrimary,
    isUnassigned,
    rackId: node.rackId,
    rackName,
    rowId: node.rowId,
    rowName,
    roomId: node.roomId,
    roomName,
    location: [roomName, rowName, rackName].join('/'),
    spaces,
    status,
    statusText: NODES_STATUS_MAP[status].text,
    statusIcon: inProgress ? STATUS_ICON_LOADING : NODES_STATUS_MAP[status].icon,
    services,
    ipV4Addresses,
    ipV6Addresses,
    ipAddresses,
    ipAddressesList,
    s3Ip,
    nfsIp: node.ip_addrs?.['4']?.nfs?.[0],
    managementIp,
    managementIpLong: managementIp ? ip.toLong(managementIp) : undefined,
    ram: {
      used: node.ram.t - node.ram.a,
      total: node.ram.t
    },
    ramUsage: (node.ram.t - node.ram.a) / node.ram.t,
    cpu: node.cpu,
    tagTypeMap: NODE_SERVICE_TAG_TYPE
  };
}

function prepareTreeNode({ id, name, domain }) {
  return {
    id,
    label: name,
    key: `${domain}:${id}`,
    data: {
      domain,
      id
    }
  };
}

function collectStatistics(nodes) {
  return nodes.reduce((memo, node) => {
    const result = {
      healthyNodes: memo.healthyNodes + (node.isHealthy ? 1 : 0),
      unhealthyNodes: memo.unhealthyNodes + (node.isFailed ? 1 : 0),
      totalNodes: memo.totalNodes + 1,
      spaces: memo.spaces
    };

    Object.entries(node.spaces).forEach(([tier, space]) => {
      if (!Object.hasOwn(result.spaces, tier)) {
        result.spaces[tier] = { used: 0, available: 0, total: 0, ratio: 0 };
      }

      result.spaces[tier].used += space.used;
      result.spaces[tier].available += space.available;
      result.spaces[tier].total += space.total;
      result.spaces[tier].ratio = (result.spaces[tier].used / result.spaces[tier].total) * 100;
    });

    return result;
  }, {
    healthyNodes: 0,
    unhealthyNodes: 0,
    totalNodes: 0,
    spaces: {}
  });
}

export default {
  name: 'locations',
  namespaced: true,
  state: getInitialState,
  getters: {
    isLoading: (state) => state.loading,
    allNodes: (state, getters) => state.nodes.map(node => prepareNode(node, getters)),
    s3Nodes: (state, getters) => getters.allNodes.filter(node => node.isInS3),
    nfsNodes: (state, getters) => getters.allNodes.filter(node => node.isInNFS),
    fileStorageNodes: (state, getters) => getters.allNodes.filter(node => node.isInFileStorage),
    abgwNodes: (state, getters) => getters.allNodes.filter(node => node.isInABGW),
    haNodes: (state, getters) => getters.allNodes.filter(node => node.isInHa),
    primaryNode: (state, getters) => getters.allNodes.find(node => node.isPrimary),
    healthyNodes: (state, getters) => getters.allNodes.filter(node => node.isHealthy),
    unhealthyNodes: (state, getters) => getters.allNodes.filter(node => node.isFailed),
    maintenanceNodes: (state, getters) => getters.allNodes.filter(node => node.isMaintenance),
    inProgressNodes: (state, getters) => getters.allNodes.filter(node => node.inProgress),
    unassignedNodes: (state, getters) => getters.allNodes.filter(node => node.isUnassigned),
    hasUnassignedNodes: (state, getters) => getters.allNodes.some(node => node.isUnassigned),
    assignedNodes: (state, getters) => getters.allNodes.filter(node => !node.isUnassigned),
    healthyNodesStatus: (state, getters) => getters.allNodes.filter(node => node.status === NODES_STATUSES_V3.HEALTHY),
    unhealthyNodesStatus: (state, getters) => getters.allNodes.filter(node => node.status === NODES_STATUSES_V3.FAILED),
    unassignedNodesStatus: (state, getters) => getters.allNodes.filter(node => node.status === NODES_STATUSES_V3.UNASSIGNED),
    recoveringNodesStatus: (state, getters) => getters.allNodes.filter(node => node.status === NODES_STATUSES_V3.RECOVERING),
    recoveryFailedNodesStatus: (state, getters) => getters.allNodes.filter(node => node.status === NODES_STATUSES_V3.RECOVERY_FAILED),
    locationTree: (state, getters) => {
      const { current: cluster } = storeToRefs(useStorageClusterStore());

      const clusterNode = prepareTreeNode({
        id: cluster.value?.id,
        domain: NODES_FAILURE_DOMAINS.CLUSTER,
        name: cluster.value?.name
      });

      if (state.locations.length > 0) {
        clusterNode.children = sortBy(getters.rooms, 'name')
          .map(room => {
            const roomNode = prepareTreeNode(room);
            const rows = getters.rows(room.id);
            if (rows.length > 0) {
              roomNode.children = sortBy(rows, 'name')
                .map(row => {
                  const rowNode = prepareTreeNode(row);
                  const racks = getters.racks(row.id);
                  if (racks.length > 0) {
                    rowNode.children = sortBy(racks, 'name')
                      .map(prepareTreeNode);
                  }
                  return rowNode;
                });
            }
            return roomNode;
          });
      }
      return [clusterNode];
    },
    rooms: state => state.locations.filter(({ domain }) => domain === NODES_FAILURE_DOMAINS.ROOM),
    roomsWithDetails: (state, getters) => getters.rooms.map((room) => {
      const nodes = getters.allNodes.filter(node => node.roomId === room.id);
      const stats = collectStatistics(nodes);
      const rackCount = state.locations.filter(loc => loc.roomId === room.id && loc.domain === NODES_FAILURE_DOMAINS.RACK).length;
      const rowCount = getters.rows(room.id).length;
      return {
        ...room,
        ...stats,
        unhealthyNodesRatio: stats.totalNodes && stats.unhealthyNodes / stats.totalNodes,
        rackCount,
        rowCount,
        isDeletable: nodes.length === 0
      };
    }),
    rows: state => roomId => state.locations.filter(loc => loc.roomId === roomId && loc.domain === NODES_FAILURE_DOMAINS.ROW),
    rowsWithDetails: (state, getters) => roomId => getters.rows(roomId).map((row) => {
      const nodes = getters.allNodes.filter(node => node.rowId === row.id);
      const stats = collectStatistics(nodes);
      const rackCount = state.locations.filter(loc => loc.rowId === row.id && loc.domain === NODES_FAILURE_DOMAINS.RACK).length;
      return {
        ...row,
        ...stats,
        unhealthyNodesRatio: stats.totalNodes && stats.unhealthyNodes / stats.totalNodes,
        rackCount,
        isDeletable: nodes.length === 0
      };
    }),
    racks: state => rowId => state.locations.filter(loc => loc.rowId === rowId && loc.domain === NODES_FAILURE_DOMAINS.RACK),
    racksWithDetails: (state, getters) => rowId => getters.racks(rowId).map(rack => {
      const nodes = getters.allNodes.filter(node => node.rackId === rack.id);
      const stats = collectStatistics(nodes);
      return {
        ...rack,
        ...stats,
        unhealthyNodesRatio: stats.totalNodes && stats.unhealthyNodes / stats.totalNodes,
        isDeletable: nodes.length === 0
      };
    }),
    rackNodes: (state, getters) => rackId => getters.allNodes.filter(node => node.rackId === rackId),
    getRoomById: (state, getters) => id => getters.rooms.find(room => room.id === id),
    getRowById: state => id => state.locations.find(loc => loc.id === id && loc.domain === NODES_FAILURE_DOMAINS.ROW),
    getRackById: state => id => state.locations.find(loc => loc.id === id && loc.domain === NODES_FAILURE_DOMAINS.RACK),
    getNodeById: (state, getters) => id => getters.allNodes.find(node => node.id === id)
  },
  mutations: {
    ACTIVATE_NOTIFICATIONS(state) {
      state.activeNotifications = true;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_NODES(state, nodes) {
      state.nodes = nodes;
    },
    ADD_OR_UPDATE_NODE(state, node) {
      const storedNode = state.nodes.find(it => it.id === node.id);
      if (storedNode) {
        if (node.spaces && Object.keys(node.spaces).length === 0) {
          node.spaces = {
            0: { t: 0, a: 0 },
            1: { t: 0, a: 0 },
            2: { t: 0, a: 0 },
            3: { t: 0, a: 0 }
          };
        }
        // NOTE (mpetrov): use merge with customizer to correctly update arrays
        mergeFn(storedNode, node);
      } else {
        // New nodes always have a location of 0/0/0
        state.nodes.push({
          ...node,
          roomId: '0',
          rowId: '0',
          rackId: '0'
        });
      }
    },
    REMOVE_NODE(state, nodeId) {
      state.nodes.splice(state.nodes.findIndex(node => node.id === nodeId), 1);
    },
    SET_LOCATIONS(state, locations) {
      state.locations = locations;
    },
    ADD_ROOM(state, room) {
      state.locations.push({
        id: room.id,
        domain: NODES_FAILURE_DOMAINS.ROOM,
        name: room.name
      });
    },
    UPDATE_ROOM(state, room) {
      const storedRoom = state.locations.find(loc => loc.id === room.id && loc.domain === NODES_FAILURE_DOMAINS.ROOM);
      Object.assign(storedRoom, room);
    },
    DELETE_ROOM(state, roomId) {
      state.locations = state.locations.filter(loc => loc.domain === NODES_FAILURE_DOMAINS.ROOM ? loc.id !== roomId : loc.roomId !== roomId);
    },
    ADD_ROW(state, row) {
      state.locations.push({
        id: row.id,
        domain: NODES_FAILURE_DOMAINS.ROW,
        name: row.name,
        roomId: row.parent.toString()
      });
    },
    UPDATE_ROW(state, row) {
      const storedRow = state.locations.find(loc => loc.id === row.id && loc.domain === NODES_FAILURE_DOMAINS.ROW);
      Object.assign(storedRow, row);
    },
    DELETE_ROW(state, rowId) {
      state.locations = state.locations.filter(loc => loc.domain === NODES_FAILURE_DOMAINS.ROW ? loc.id !== rowId : loc.rowId !== rowId);
    },
    ADD_RACK(state, { rack, roomId }) {
      state.locations.push({
        id: rack.id,
        domain: NODES_FAILURE_DOMAINS.RACK,
        name: rack.name,
        rowId: rack.parent.toString(),
        roomId
      });
    },
    UPDATE_RACK(state, rack) {
      const storedRack = state.locations.find(loc => loc.id === rack.id && loc.domain === NODES_FAILURE_DOMAINS.RACK);
      Object.assign(storedRack, rack);
    },
    MOVE_TO_RACK(state, { nodeId, rackId }) {
      const node = state.nodes.find(n => n.id === nodeId);
      if (node) {
        const rack = state.locations.find(loc => loc.id === rackId && loc.domain === NODES_FAILURE_DOMAINS.RACK);
        Object.assign(node, {
          rackId,
          rowId: rack.rowId,
          roomId: rack.roomId
        });
      }
    },
    DELETE_RACK(state, rackId) {
      const index = state.locations.findIndex(loc => loc.id === rackId && loc.domain === NODES_FAILURE_DOMAINS.RACK);
      state.locations.splice(index, 1);
    }
    // todo Add FLUSH_STATE mutation
  },
  actions: {
    FETCH_LOCATIONS({ commit }) {
      commit('SET_LOADING', true);

      return this.$api.get('/api/v2/locations')
        .then(res => {
          const nodes = [];
          const locations = [];

          if (res.data.length > 0) {
            const locs = res.data;

            locs.forEach(room => {
              const roomId = room.id.toString();
              locations.push({
                id: roomId,
                domain: NODES_FAILURE_DOMAINS.ROOM,
                name: room.name
              });

              if (room.children) {
                room.children.forEach(row => {
                  const rowId = row.id.toString();

                  locations.push({
                    id: rowId,
                    domain: NODES_FAILURE_DOMAINS.ROW,
                    name: row.name,
                    roomId
                  });

                  if (row.children) {
                    row.children.forEach(rack => {
                      const rackId = rack.id.toString();

                      locations.push({
                        id: rackId,
                        domain: NODES_FAILURE_DOMAINS.RACK,
                        name: rack.name,
                        rowId,
                        roomId
                      });

                      if (rack.children) {
                        rack.children.forEach(node => {
                          nodes.push({ ...node, roomId, rowId, rackId });
                        });
                      }
                    });
                  }
                });
              }
            });
            commit('SET_LOCATIONS', locations);
          }
          commit('SET_NODES', nodes);
          commit('ACTIVATE_NOTIFICATIONS');

          return res;
        })
      .finally(() => {
        commit('SET_LOADING', false);
      });
    },
    CREATE_ROOM({ commit }, name) {
      return this.$api.post(`/api/v2/locations/${LOCATIONS_MAP.ROOM}`, { name })
        .then(res => {
          const roomData = res.data;
          commit('ADD_ROOM', { ...roomData, id: roomData.id.toString() });
          return res;
        });
    },
    RENAME_ROOM({ commit }, { name, roomId }) {
      return this.$api.patch(`/api/v2/locations/${LOCATIONS_MAP.ROOM}/${roomId}`, { name })
        .then(res => {
          commit('UPDATE_ROOM', {
            id: roomId,
            name
          });
          return res;
        });
    },
    DELETE_ROOM({ commit }, roomId) {
      return this.$api.delete(`/api/v2/locations/${LOCATIONS_MAP.ROOM}/${roomId}`)
        .then(res => {
          commit('DELETE_ROOM', roomId);
          return res;
        });
    },
    CREATE_ROW({ commit }, { name, roomId }) {
      return this.$api.post(`/api/v2/locations/${LOCATIONS_MAP.ROOM}/${roomId}/${LOCATIONS_MAP.ROW}`, { name })
        .then(res => {
          const rowData = res.data;
          commit('ADD_ROW', { ...rowData, id: rowData.id.toString() });
          return res;
        });
    },
    RENAME_ROW({ commit }, { name, rowId }) {
      return this.$api.patch(`/api/v2/locations/${LOCATIONS_MAP.ROW}/${rowId}`, { name })
        .then(res => {
          commit('UPDATE_ROW', {
            id: rowId,
            name
          });
          return res;
        });
    },
    DELETE_ROW({ commit }, rowId) {
      return this.$api.delete(`/api/v2/locations/${LOCATIONS_MAP.ROW}/${rowId}`)
        .then(res => {
          commit('DELETE_ROW', rowId);
          return res;
        });
    },
    CREATE_RACK({ commit, getters }, { name, rowId }) {
      return this.$api.post(`/api/v2/locations/${LOCATIONS_MAP.ROW}/${rowId}/${LOCATIONS_MAP.RACK}`, { name })
        .then(res => {
          const row = getters.getRowById(rowId);
          if (row) {
            const rackData = res.data;
            commit('ADD_RACK', { rack: { ...rackData, id: rackData.id.toString() }, roomId: row.roomId });
          }
          return res;
        });
    },
    MOVE_TO_RACK({ commit }, { rackId, nodeIds }) {
      return this.$api.put(`/api/v2/locations/${LOCATIONS_MAP.RACK}/${rackId}/nodes`, nodeIds)
        .then(res => {
          nodeIds.forEach(nodeId => commit('MOVE_TO_RACK', { nodeId, rackId }));
          return res;
        });
    },
    RENAME_RACK({ commit }, { name, rackId }) {
      return this.$api.patch(`/api/v2/locations/${LOCATIONS_MAP.RACK}/${rackId}`, { name })
        .then(res => {
          commit('UPDATE_RACK', {
            id: rackId,
            name
          });
          return res;
        });
    },
    DELETE_RACK({ commit }, rackId) {
      return this.$api.delete(`/api/v2/locations/${LOCATIONS_MAP.RACK}/${rackId}`)
        .then(res => {
          commit('DELETE_RACK', rackId);
          return res;
        });
    },
    RETRY_RECOVERY(_, nodeId) {
      return this.$api.post(`/api/v2/nodes/${nodeId}/reinstall/`);
    },
    CANCEL_RECOVERY(_, nodeId) {
      return this.$api.delete(`/api/v2/nodes/${nodeId}/reinstall/`);
    },
    socket_nodePresentation({ commit, state }, { data, operation }) {
      if (state.activeNotifications && ['changed', 'created'].includes(operation) && Array.isArray(data)) {
        data.forEach(node => commit('ADD_OR_UPDATE_NODE', {
          ...node,
          id: node.id.toString()
        }));
      }
    },
    socket_tasks({ commit, state }, { task_id: taskId, state: taskState }) {
      const isNodeTaskRunning = state.nodes.find(node => node.tasks?.includes(taskId));
      if (isNodeTaskRunning && taskState === TaskState.SUCCESS) {
        const nodes = state.nodes.map(node => ({ ...node, tasks: node.tasks?.filter(id => id !== taskId) }));
        commit('SET_NODES', nodes);
      }
    }
  }
};
